import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../_shared/lib/downloadBlob';
import type { DevStandardsDocument } from '../types/devStandards';

interface StandardsDownloadButtonsProps {
  doc: DevStandardsDocument;
  markdownContainerId?: string;
}

export function StandardsDownloadButtons({
  doc,
  markdownContainerId = 'standards-markdown-view',
}: StandardsDownloadButtonsProps) {
  const { t } = useTranslation();

  const handleMdDownload = () => {
    const blob = new Blob([doc.markdownText], { type: 'text/markdown;charset=utf-8' });
    downloadBlob(blob, `dev-standards-${doc.docVersion}.md`);
  };

  const handleHtmlDownload = () => {
    const container = document.getElementById(markdownContainerId);
    const innerHtml = container?.outerHTML ?? `<pre>${doc.markdownText}</pre>`;
    const fullHtml = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>개발표준 통합 가이드 — ${doc.docVersion}</title>
  <style>
    body { font-family: system-ui, -apple-system, sans-serif; max-width: 900px; margin: 40px auto; padding: 0 24px; line-height: 1.7; color: #0F172A; }
    pre, code { font-family: Consolas, Menlo, monospace; background: #F8FAFC; }
    pre { padding: 16px; border-radius: 8px; overflow-x: auto; }
    code { padding: 0.1em 0.35em; border-radius: 4px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 8px 12px; border-bottom: 1px solid #E2E8F0; }
    blockquote { border-left: 4px solid #6200EE; margin-left: 0; padding-left: 16px; color: #475569; }
    hr { border: none; border-top: 1px solid #E2E8F0; margin: 24px 0; }
  </style>
</head>
<body>
${innerHtml}
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
    downloadBlob(blob, `dev-standards-${doc.docVersion}.html`);
  };

  const btnStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--color-surface-background)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'var(--font-family-sans)',
    cursor: 'pointer',
    transition: 'border-color 0.15s, color 0.15s',
  };

  return (
    <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
      <button
        type="button"
        onClick={handleMdDownload}
        style={btnStyle}
        aria-label={t('devStandardsViewer.download.md')}
        data-testid="download-md-btn"
      >
        {t('devStandardsViewer.download.md')}
      </button>
      <button
        type="button"
        onClick={handleHtmlDownload}
        style={btnStyle}
        aria-label={t('devStandardsViewer.download.html')}
        data-testid="download-html-btn"
      >
        {t('devStandardsViewer.download.html')}
      </button>
    </div>
  );
}
